import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { listReviews, upsertReview, deleteReview } from "@/lib/reviews.functions";
import { useAuth } from "@/lib/auth";

type Props = { productId?: string; vendorId?: string };

function Stars({ value, size = "text-base" }: { value: number; size?: string }) {
  return (
    <span className={`${size} tracking-tight text-accent`} aria-label={`${value} od 5`}>
      {"★★★★★".slice(0, value)}
      <span className="text-muted-foreground">{"★★★★★".slice(value)}</span>
    </span>
  );
}

export function Reviews({ productId, vendorId }: Props) {
  const { user } = useAuth();
  const fetchReviews = useServerFn(listReviews);
  const submitReview = useServerFn(upsertReview);
  const removeReview = useServerFn(deleteReview);
  const qc = useQueryClient();

  const key = ["reviews", productId ?? vendorId];
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => fetchReviews({ data: { productId, vendorId } }),
  });

  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [hover, setHover] = useState(0);

  const mutation = useMutation({
    mutationFn: () => submitReview({ data: { productId, vendorId, rating, body: body.trim() || undefined } }),
    onSuccess: () => {
      setBody("");
      qc.invalidateQueries({ queryKey: key });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => removeReview({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const reviews = data?.reviews ?? [];
  const avg = data?.avg ?? 0;
  const count = data?.count ?? 0;

  return (
    <div className="mt-16 border-t border-border pt-12">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <h2 className="font-serif text-3xl">Ocene in mnenja</h2>
        {count > 0 && (
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Stars value={Math.round(avg)} />
            <span>{avg.toFixed(1)} / 5 · {count} {count === 1 ? "ocena" : count < 5 ? "ocene" : "ocen"}</span>
          </div>
        )}
      </div>

      {user ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="mt-8 border border-border p-6 bg-card"
        >
          <div className="text-sm font-medium mb-2">Oddaj svojo oceno</div>
          <div className="flex gap-1 mb-4" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                className="text-2xl text-accent leading-none"
                aria-label={`${n} zvezdic`}
              >
                {(hover || rating) >= n ? "★" : "☆"}
              </button>
            ))}
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Vaše mnenje (neobvezno)…"
            rows={3}
            maxLength={2000}
            className="w-full border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
          />
          {mutation.error && (
            <div className="mt-2 text-xs text-destructive">
              {(mutation.error as Error).message}
            </div>
          )}
          <button
            type="submit"
            disabled={mutation.isPending}
            className="mt-3 bg-primary text-primary-foreground px-5 py-2 text-sm tracking-wide hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? "Pošiljam…" : "Objavi oceno"}
          </button>
          <p className="mt-2 text-xs text-muted-foreground">
            Če ste že oddali oceno, bo ta posodobljena.
          </p>
        </form>
      ) : (
        <div className="mt-8 text-sm text-muted-foreground">
          <Link to="/prijava" className="underline underline-offset-4 hover:text-accent">
            Prijavite se
          </Link>{" "}
          za oddajo ocene.
        </div>
      )}

      <div className="mt-10 space-y-6">
        {reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ni še ocen. Bodite prvi!</p>
        ) : (
          reviews.map((r) => (
            <div key={r.id} className="border-b border-border pb-6">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Stars value={r.rating} size="text-sm" />
                  <span className="text-sm font-medium">{r.author_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString("sl-SI")}
                  </span>
                </div>
              </div>
              {r.body && (
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {r.body}
                </p>
              )}
              {user && (
                <button
                  type="button"
                  onClick={() => deleteMut.mutate(r.id)}
                  className="mt-2 text-xs text-muted-foreground hover:text-destructive underline-offset-4 hover:underline"
                >
                  izbriši (če je tvoja)
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
